/**
 * Mock del visor de ficheros de la ZFT — GEN-22 #323 (eje A del owner).
 * Ref: services/kernel/kernel/zft_api.py + zft_store.py (backend real).
 *
 * Reproduce contra mock las MISMAS formas que el kernel para que el front
 * ejercite el visor sin backend:
 *   - listar(zona, path) -> { zona, path, vista, entries:[{nombre,tipo,tamano,modificado}] }
 *   - subir a inbox       -> { ok, zona, path, entry }
 *   - descargar           -> { blob, filename }  (outputs/archive directo; raw exige grant)
 *
 * Solo las CUATRO zonas del eje A (raw/inbox/outputs/archive). Las zonas DIKW,
 * Kernel, Digital Team y Workspace NO son navegables — aquí ni existen, igual
 * que el kernel responde 403 zona_no_navegable. La lectura de `raw` NO es
 * directa: pasa por el flujo tutelado (proposeFileAction -> approve), cuyo grant
 * valida `rawGrant()` de actions-store.js — espejo del backend.
 *
 * El árbol calca el seed de zft_store._semilla() para que mock y real coincidan.
 */

// Modo de vista del owner por zona (espejo de zft.vista_owner del kernel).
const VISTA = {
  raw: 'tutelado',   // se lista (es su material) pero leer/descargar es tutelado
  inbox: 'subida',   // el owner sube aquí (directo)
  outputs: 'lectura',
  archive: 'lectura',
}

const _TS = '2026-07-20T09:00:00.000Z'

// Árbol sembrado por zona: { subpath -> { content, contentType } }. Calca el
// seed del backend (zft_store._semilla).
function _seed() {
  const md = 'text/markdown'
  const pdf = 'application/pdf'
  return {
    raw: {
      'contratos/contrato-marco-koana-2026.pdf': { content: '%PDF-1.4 (contrato)\n', contentType: pdf },
      'escaneos/acta-notarial.pdf': { content: '%PDF-1.4 (acta)\n', contentType: pdf },
      'notas-privadas.md': { content: '# Notas privadas del owner\n\nMaterial en crudo, zona L5.', contentType: md },
    },
    inbox: {
      'facturas/factura-endesa-julio.pdf': { content: '%PDF-1.4 (factura)\n', contentType: pdf },
      'para-procesar/borrador-informe.md': { content: '# Borrador\n\nPendiente de procesar por el equipo.', contentType: md },
    },
    outputs: {
      'informe-operaciones-2026Q2.md': { content: '# Informe de operaciones — 2026 Q2\n\nEntregable generado por el equipo.', contentType: md },
      'resumenes/resumen-koana.md': { content: '# Resumen ejecutivo — Koana\n\nSíntesis de onboarding.', contentType: md },
    },
    archive: {
      '2025/informe-anual-2025.pdf': { content: '%PDF-1.4 (anual 2025)\n', contentType: pdf },
      'actas/acta-comite-2026-06.md': { content: '# Acta de comité — 2026-06\n\nArchivado (frío).', contentType: md },
    },
  }
}

let _fs = _seed()

const _norm = p => (p || '').replace(/\\/g, '/').split('/').filter(Boolean).join('/')
const _bytes = s => (typeof s === 'string' ? new TextEncoder().encode(s).length : s.byteLength ?? 0)

export function zonaVista(zona) {
  return VISTA[zona] ?? null
}

/** Lista el contenido inmediato (carpetas + ficheros) de una zona/subruta. */
export function listFilesMock(zona, path = '') {
  if (!(zona in _fs)) throw new Error(`zona_no_navegable:${zona}`)
  const base = _norm(path)
  const prefix = base ? base + '/' : ''
  const dirs = new Set()
  const files = []
  for (const [p, meta] of Object.entries(_fs[zona])) {
    if (prefix && !p.startsWith(prefix)) continue
    const rest = p.slice(prefix.length)
    if (!rest) continue
    if (rest.includes('/')) {
      dirs.add(rest.split('/')[0])
    } else {
      files.push({ nombre: rest, tipo: 'file', tamano: _bytes(meta.content), modificado: _TS })
    }
  }
  const entries = [
    ...[...dirs].map(d => ({ nombre: d, tipo: 'dir' })),
    ...files,
  ].sort((a, b) => (a.tipo !== 'dir') - (b.tipo !== 'dir') || a.nombre.localeCompare(b.nombre))
  return { zona, path: base, vista: VISTA[zona], entries }
}

/** Sube un fichero a inbox (directo). */
export function uploadInboxMock(zona, filename, path = '', content = '') {
  if (zona !== 'inbox') throw new Error('zona_no_navegable')
  const nombre = (filename || '').replace(/\\/g, '/').split('/').pop()
  const subpath = _norm(path ? `${path}/${nombre}` : nombre)
  _fs.inbox[subpath] = { content, contentType: 'application/octet-stream' }
  return { ok: true, zona, path: subpath, entry: { nombre, tipo: 'file', tamano: _bytes(content), modificado: _TS } }
}

/** Lee un fichero (bytes + tipo). No aplica política: el gate lo pone client.js. */
export function readFileMock(zona, path) {
  const meta = _fs[zona]?.[_norm(path)]
  if (!meta) throw new Error('fichero_no_encontrado')
  return meta
}

// Política de gobierno de la ZFT (#322) — espejo de `dktec.json` (VoBo Guo
// 2026-07-23: taxonomía + retención decididas por Cassie/Max). El mock la refleja
// para que el visor demuestre las "carpetas sugeridas"; en real llega del kernel.
export function getFilePolicyMock() {
  return {
    taxonomy: {
      inbox: ['facturas', 'contratos', 'documentacion', 'por-clasificar'],
      outputs: ['comercial', 'finanzas', 'legal', 'backoffice', 'informes', 'infraestructura'],
    },
    retention: {
      outputs: { dias: 365, accion: 'archivar' },
      archive: { dias: 2555, accion: 'expirar' },
    },
  }
}

// Solo tests: restablece el árbol entre casos.
export function __resetMockFiles() {
  _fs = _seed()
}
